import { Component, OnInit, Input, Output, ElementRef } from '@angular/core';
import { Node, TreeTableNode, Options, SearchableNode, NodeInTree } from '../models';
import { TreeService } from '../services/tree/tree.service';
import { MatTableDataSource } from '@angular/material/table';
import { ValidatorService } from '../services/validator/validator.service';
import { ConverterService } from '../services/converter/converter.service';
import { defaultOptions } from '../default.options';
import { flatMap, defaults } from 'lodash-es';
import { Subject } from 'rxjs';
import { Option } from 'fp-ts/lib/Option';
import { UserpilotReloader } from '../../../../../helper/userpilot-reloader.component';

@Component({
  selector: 'ng-treetable, treetable', // 'ng-treetable' is currently being deprecated
  templateUrl: './treetable.component.html',
  styleUrls: ['./treetable.component.scss']
})
export class TreetableComponent<T> extends UserpilotReloader implements OnInit {
  @Input() tree: Node<T> | Node<T>[];
  @Input() options: Options<T> = {};
  @Output() nodeClicked: Subject<TreeTableNode<T>> = new Subject();
  private searchableTree: SearchableNode<T>[];
  private treeTable: TreeTableNode<T>[];
  displayedColumns: string[];
  dataSource: MatTableDataSource<TreeTableNode<T>>;

  constructor(
    private treeService: TreeService,
    private validatorService: ValidatorService,
    private converterService: ConverterService,
    elem: ElementRef
  ) {
    super();
    const tagName = elem.nativeElement.tagName.toLowerCase();
    if (tagName === 'ng-treetable') {
      console.warn(`DEPRECATION WARNING: \n The 'ng-treetable' selector is being deprecated. Please use the new 'treetable' selector`);
    }
  }

  ngOnInit() {
    this.tree = Array.isArray(this.tree) ? this.tree : [this.tree];
    this.options = this.parseOptions(defaultOptions);
    const root = this.tree[0];
    const customOrderValidator = this.validatorService.validateCustomOrder<T, Node<T>>(root, this.options.customColumnOrder);
    if (this.options.customColumnOrder && !customOrderValidator.valid) {
      if (!(customOrderValidator.xor.includes('INFO_TOOLTIP') && customOrderValidator.xor.length === 1)) {
        customOrderValidator.xor = customOrderValidator.xor.filter(x => x !== 'INFO_TOOLTIP');
        throw new Error(`
          Properties ${customOrderValidator.xor.map(x => `'${x}'`).join(', ')} incorrect or missing in customColumnOrder`
        );
      }
    }
    this.displayedColumns = this.options.customColumnOrder
      ? this.options.customColumnOrder
      : this.extractNodeProps(this.tree[0]);
    this.searchableTree = this.tree.map(t => this.converterService.toSearchableTree(t));
    const treeTableTree = this.searchableTree.map(st => this.converterService.toTreeTableTree(st, this.options));
    this.treeTable = flatMap(treeTableTree, this.treeService.flatten);
    this.dataSource = this.generateDataSource();
    this.foldTree();
  }

  extractNodeProps(tree: Node<T> & { value: { [k: string]: any } }): string[] {
    return Object.keys(tree.value).filter(x => typeof tree.value[x] !== 'object');
  }

  generateDataSource(): MatTableDataSource<TreeTableNode<T>> {
    return new MatTableDataSource(this.treeTable.filter(x => x.isVisible));
  }

  formatIndentation(node: TreeTableNode<T>, step: number = 5): string {
    return '&nbsp;'.repeat(node.depth * step);
  }

	formatElevation(): string {
		return `mat-elevation-z${this.options.elevation}`;
	}

  onNodeClick(clickedNode: TreeTableNode<T>): void {
    clickedNode.isExpanded = !clickedNode.isExpanded;
    this.foldTree();
    this.nodeClicked.next(clickedNode);
  }

  // Overrides default options with those specified by the user
  parseOptions(defaultOpts: Options<T>): Options<T> {
    return defaults(this.options, defaultOpts);
  }

  private foldTree() {
    this.treeTable.forEach(el => {
      el.isVisible = this.searchableTree.every((st: SearchableNode<T>) => {
        const foundNode: Option<NodeInTree<any>> = this.treeService.searchById(st, el.id);
        let path = [];
        if (foundNode && foundNode['value'] && foundNode['value']['pathToRoot']) {
          path = foundNode['value']['pathToRoot']
        }
        return path.every(p => this.treeTable.find(x => x.id === p.id).isExpanded);
      });
    });
    this.dataSource = this.generateDataSource();
  }
}
